import { useEffect, useMemo, useState } from "react";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { FormatDateTime } from "../../../../../utils/functions/ResourceFunctions";
import { useSearchFilter } from "../../../../../utils/functions/useDate";
import {
  useDeleteScoreBasedRecommendationMutation,
  useGetScoreRecommwndationQuery,
} from "../../../../api/hrApi";
import DashboardStyle from "../../../../dashboard/Styles/Dashboard.module.css";
import { CTAButtons } from "../../../../global/components/Buttons/buttons";
import PageStyle from "../../../Components/Layout/PageLayout";
import { TableActions } from "../../../Components/Misc/Actions";
import DeleteItemModal from "../../../Components/Modals/DeleteItemModal";
import { AppModalTemplate } from "../../../Components/Modals/Modals";
import Pagination from "../../../Components/Pagination/Pagination";
import {
  CalendarFilter2,
  ProDropFilter,
  SearchFilter,
} from "../../../Components/Search/Search";
import Table from "../../../Components/Table/Table";
import { getStatusText } from "../../Finance/CostCenter/Class";
import EditScoreBasedModal from "./Modals/EditScoreBasedModal";
import ScoreBaseModal from "./Modals/ScoreBaseModal";
import { DEFINED_PERMISSIONS } from "../../../../../utils/const";
import { FilterByStatus } from "./status";
const ScoreRecommendation = () => {
  const [onDelete] = useDeleteScoreBasedRecommendationMutation();
  const [openModal, setOpenModal] = useState(false);
  const [openCreatModal, setOpenCreatModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [matchedPermissionHr, setMatchedPermissionHr] = useState("");
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  useEffect(() => {
    const userPerm = JSON.parse(localStorage.getItem("x-u-perm"));
    const hrPerm = [
      DEFINED_PERMISSIONS.SeniorHRManager,
      DEFINED_PERMISSIONS.PrimaryHRManager,
    ];

    // Check if userPerm exists and matches any permission in hrPerm
    if (userPerm && userPerm.some((perm) => hrPerm.includes(perm))) {
      const matchedPerm = userPerm.find((perm) => hrPerm.includes(perm));
      setMatchedPermissionHr(matchedPerm); // Set matchedPermissionHr to the matched permission
    } else {
      setMatchedPermissionHr(""); // Reset to empty string if not matched
    }
  }, []);
  const navigate = useNavigate();
  const handleModal = () => {
    setOpenModal(!openModal);
  };
  const handleOpenCreateModale = () => {
    setOpenCreatModal(!openCreatModal);
  };
  const {
    searchText,
    setSearchText,
    pageSize,
    setPageSize,
    setCurrentPage,
    currentPage,
    filter,
    setFilter,
  } = useSearchFilter({
    initialFilter: 1,
  });
  const { data, isLoading } = useGetScoreRecommwndationQuery({
    PageSize: pageSize,
    PageNumber: currentPage,
    GeneralSearch: searchText,
    StartDate: startDate,
    EndDate: endDate,
    ApprovalStatus: filter,
  });
  const actionButton = (
    <>
      <CTAButtons onClick={handleOpenCreateModale}>
        Create Score Based Recommendation
      </CTAButtons>
    </>
  );
  const handleOpenDeleteModal = (id) => {
    if (id) {
      setSelectedId(id);
      setOpenDelete(true);
    }
  };
  const handleCloseDelete = () => {
    setSelectedId(null);
    setOpenDelete(false);
  };
  const handleDelete = async () => {
    try {
      const response = await onDelete({
        id: selectedId,
      }).unwrap();
      if (response?.successful) {
        setOpenDelete(false);
        toast.success(response?.statusMessage);
      }
    } catch (error) {
      toast.error(
        error?.data?.statusMessage ??
          "Error deleting Score Based Recommendation"
      );
    }
  };
  const handleView = (id) => {
    if (id) {
      navigate(`${id}/view`);
    }
  };
  const handleEdit = (id) => {
    if (id) {
      setSelectedId(id);
      handleModal();
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!data?.result) return [];
    return [...data.result].sort((a, b) => {
      if (!sortColumn) return 0;
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];

      if (sortColumn === "fromScore" || sortColumn === "toScore") {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }

      if (sortColumn === "createdDate") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [data?.result, sortColumn, sortDirection]);

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort className="ml-1" />;
    return sortDirection === "asc" ? (
      <FaSortUp className="ml-1" />
    ) : (
      <FaSortDown className="ml-1" />
    );
  };

  return (
    <PageStyle
      action={actionButton}
      hasBack={true}
      backURL={"/dashboard/hr/HR-Appraisal-Setup"}
      hasTopPageDemo
      support={
        "Helps the system suggest appropriate actions based on performances score"
      }
      demoTitle={"SCORE BASE RECOMMENDATION"}
    >
      <form className={DashboardStyle.dashboard_filter}>
        <SearchFilter
          value={searchText}
          text={(searchText) => setSearchText(searchText)}
        />
        <CalendarFilter2
          name="Select Date Range"
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          normalDate
        />
        <ProDropFilter
          filter={filter}
          setFilter={setFilter}
          name={"Status"}
          filterBy={FilterByStatus}
        />
      </form>
      <div className={DashboardStyle.dashboard_table_holder}>
        <Table>
          <thead>
            <tr>
              <th>S/N</th>
              <th>Recommendation Title</th>
              <th>From</th>
              <th>To</th>
              <th>Description</th>
              <th>Initiator's Name</th>
              <th>Date Created</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="text-center">
                  Loading...
                </td>
              </tr>
            ) : data?.result?.length > 0 ? (
              data?.result?.map((item, index) => {
                return (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{item?.recommendationTitle}</td>
                    <td>{item?.fromScore}</td>
                    <td>{item?.toScore}</td>
                    <td>{item?.recommendationDescription}</td>
                    <td>{item?.createdBy}</td>
                    <td>{FormatDateTime(item.createdDate, "DD/MM/YYYY")}</td>
                    <td>
                      {typeof item?.approvalStatus === "number"
                        ? getStatusText(item?.approvalStatus)
                        : item?.approvalStatus}
                    </td>
                    <td>
                      <TableActions
                        hasChildren={true}
                        url={matchedPermissionHr ? `${item.id}/view` : null}
                      >
                        {[
                          {
                            name: item?.approvalStatus !== 0 ? "Edit" : null,
                            action:
                              item?.approvalStatus !== 0
                                ? () => handleEdit(item.id)
                                : null,
                          },
                          {
                            name: matchedPermissionHr ? "View" : null,
                            action: () =>
                              matchedPermissionHr ? handleView(item.id) : null,
                          },
                          {
                            name: "Delete",
                            action: () => handleOpenDeleteModal(item.id),
                          },
                        ]}
                      </TableActions>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6} className="text-center">
                  No Rating Scales Found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <Pagination
        last_page={data?.totalPages}
        present_page={data?.currentPage}
        totalRows={data?.totalRows}
        pageSize={pageSize}
        setPageSize={(page) => setPageSize(page)}
        click={(page) => setCurrentPage(page)}
      />

      {openModal && selectedId && (
        <AppModalTemplate
          width={"600px"}
          isOpen={openModal}
          setIsOpen={handleModal}
        >
          <EditScoreBasedModal
            isOpen={openModal}
            setIsOpen={setOpenModal}
            onValidate={handleModal}
            id={selectedId}
          />
        </AppModalTemplate>
      )}
      {openCreatModal && (
        <AppModalTemplate
          width={"600px"}
          isOpen={openCreatModal}
          setIsOpen={handleOpenCreateModale}
        >
          <ScoreBaseModal
            isOpen={openCreatModal}
            setIsOpen={setOpenCreatModal}
            onValidate={handleOpenCreateModale}
          />
        </AppModalTemplate>
      )}

      <DeleteItemModal
        isOpen={openDelete}
        handleClose={handleCloseDelete}
        handleDelete={handleDelete}
        headText={"Delete Score Based Recommendation"}
        infoText={
          "Are you sure you want to Delete this Score Based Recommendation?"
        }
      />
    </PageStyle>
  );
};

export default ScoreRecommendation;
